import { PrimaryButton, useSnackbar } from "@deathstar/ui";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import api from "../../api/api";
import { useAccountId } from "../../api/useAccountId";
import { ChaseManualVerificationTip } from "./ChaseManualVerificationTip";
import { stripePromise } from "./NewPaymentMethodForm";

const formatDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/**
 * Shown to account admins when a bank account's authorization (e.g. Chase time-limited consent) is about to expire.
 * Relinking the same account refreshes the existing payment method, so auto-pay settings don't change.
 */
export default function BankRelinkBanner() {
    const accountId = useAccountId();
    const { data, refetch } = api.billing.useProfile(accountId!, { retry: false });
    const [relinkingId, setRelinkingId] = useState<string | null>(null);
    const [dismissed, setDismissed] = useState<string[]>([]);

    const expiring = data?.paymentMethods.filter((pm) => pm.relinkRequired && !dismissed.includes(pm.id)) || [];
    if (!expiring.length) return null;

    const relink = async (paymentMethodId: string) => {
        setRelinkingId(paymentMethodId);
        try {
            const [stripe, { clientSecret }] = await Promise.all([
                stripePromise,
                api.billing.createRelinkSession(accountId!, paymentMethodId),
            ]);
            if (!stripe) throw new Error("Stripe failed to load");

            const { financialConnectionsSession, error } = await stripe.collectFinancialConnectionsAccounts({ clientSecret });
            if (error) throw new Error(error.message);

            // The browser-side session doesn't include `relink_result`, so the server checks whether the account was relinked
            const relinked =
                !!financialConnectionsSession &&
                (await api.billing.completeRelinkSession(accountId!, paymentMethodId, financialConnectionsSession.id).then(
                    () => true,
                    (e) => {
                        console.error(e);
                        return false;
                    }
                ));
            if (!relinked) {
                useSnackbar.add(
                    "Your bank account was not reconnected. Please try again, or add a new payment method on the Billing page.",
                    {
                        variant: "error",
                        autoHideDuration: 10000,
                    }
                );
                return;
            }

            useSnackbar.add("Your bank account has been reconnected.", { variant: "success", autoHideDuration: 10000 });
            refetch();
        } catch (e) {
            console.error(e);
            useSnackbar.add(e instanceof Error && e.message ? e.message : "Failed to reconnect your bank account", { variant: "error" });
        } finally {
            setRelinkingId(null);
        }
    };

    return (
        <div className="space-y-2 px-4 pt-4">
            {expiring.map((pm) => (
                <div
                    key={pm.id}
                    className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                >
                    <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-500" />
                    <div className="min-w-0 flex-1 space-y-0.5">
                        <p className="font-semibold">Reconnect your bank account</p>
                        <p>
                            Your bank's authorization for the {pm.description} expires{" "}
                            {pm.relinkExpiresAt ? `on ${formatDate(pm.relinkExpiresAt)}` : "soon"}. Reconnect it to keep payments working.
                        </p>
                        <ChaseManualVerificationTip className="text-xs text-amber-800" />
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <PrimaryButton disabled={!!relinkingId} onClick={() => relink(pm.id)}>
                            {relinkingId === pm.id ? "Reconnecting..." : "Reconnect"}
                        </PrimaryButton>
                        <button
                            title="Dismiss"
                            className="rounded-full p-1 text-amber-700 hover:bg-amber-100"
                            onClick={() => setDismissed((ids) => [...ids, pm.id])}
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}
